/**
 * Die Betriebsart „Felder aktualisieren" (Umsetzungsplan §10, Phase 11).
 *
 * 🔴 Das ist der gefährlichste Knopf des Programms. Die Neuanlage lässt sich durch
 * Deaktivieren zurücknehmen — ein überschriebener Bestandswert nicht. Deshalb:
 *
 *   · Voreingestellt wird nur gefüllt, wo Plenty leer oder 0 ist. „Überschreiben"
 *     ist je Feld ein eigener Haken.
 *   · Vor dem Lauf steht eine Vorschau mit Vorher/Nachher — live aus Plenty gelesen.
 *   · Eine Tippbestätigung, und zwar eine andere als bei der Anlage.
 *   · Danach ein feldgenauer Rollback aus dem Schnappschuss.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, Images, PencilLine, Undo2 } from 'lucide-react';
import { api } from '@libero/core/services/api';
import LaufButton from './LaufButton';
import Schritt from './Schritt';
import { zahl } from '../lib/format';
import type {
  FelderAntwort, FeldAuswahl, FeldRollbackBericht, FtpOrdner, NachlieferBericht,
  UpdateVorschau,
} from '../lib/typen';

/**
 * Wie viele Varianten die Vorher/Nachher-Liste zeigt.
 *
 * Der Deckel bleibt — ein paar tausend Zeilen im DOM machen die Seite unbenutzbar.
 * Neu ist, dass er dransteht: eine stillschweigend abgeschnittene Liste liest sich
 * wie „das ist alles".
 */
const ZEILEN_DECKEL = 200;

export default function AktualisierenPanel({
  entwurfId, bildOrdner, schrittAb = 2,
}: {
  entwurfId: number;
  bildOrdner: FtpOrdner[];
  /** Nummer des ersten Schritts in diesem Panel — die Seite davor zaehlt vor. */
  schrittAb?: number;
}) {
  const [katalog, setKatalog] = useState<FelderAntwort | null>(null);
  const [auswahl, setAuswahl] = useState<FeldAuswahl[]>([]);
  const [vorschau, setVorschau] = useState<UpdateVorschau | null>(null);
  const [bestaetigung, setBestaetigung] = useState('');
  const [laufId, setLaufId] = useState<number | null>(null);
  const [rollback, setRollback] = useState<FeldRollbackBericht | null>(null);
  const [nachzug, setNachzug] = useState<NachlieferBericht | null>(null);
  const [nachzugOrdner, setNachzugOrdner] = useState('');
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    void api.get<{ data: FelderAntwort }>('/programmes/artikelimport/felder')
      .then(a => setKatalog(a.data.data))
      .catch(() => setFehler('Die Feldliste ließ sich nicht laden.'));
  }, []);

  const gewaehlt = useCallback(
    (feld: string) => auswahl.find(a => a.feld === feld),
    [auswahl],
  );

  const umschalten = (feld: string) => {
    setVorschau(null);
    setAuswahl(v => (v.some(a => a.feld === feld)
      ? v.filter(a => a.feld !== feld)
      : [...v, { feld }]));
  };

  const ueberschreibenUmschalten = (feld: string) => {
    setVorschau(null);
    setAuswahl(v => v.map(a => (a.feld === feld ? { ...a, ueberschreiben: !a.ueberschreiben } : a)));
  };

  const rufen = useCallback(async <T,>(
    was: string, pfad: string, koerper: Record<string, unknown>,
  ): Promise<T | null> => {
    setLaeuft(was);
    setFehler(null);
    try {
      const antwort = await api.post<{ data: T }>(pfad, koerper);
      return antwort.data.data;
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? `${was} fehlgeschlagen.`);
      return null;
    } finally {
      setLaeuft(null);
    }
  }, []);

  const vorschauHolen = useCallback(async () => {
    const ergebnis = await rufen<UpdateVorschau>(
      'Vorschau', `/programmes/artikelimport/entwuerfe/${entwurfId}/felder-vorschau`,
      { felder: auswahl },
    );
    if (ergebnis) setVorschau(ergebnis);
  }, [rufen, entwurfId, auswahl]);

  const aktualisieren = useCallback(async () => {
    const ergebnis = await rufen<{ laufId: number }>(
      'Aktualisieren', `/programmes/artikelimport/entwuerfe/${entwurfId}/felder-aktualisieren`,
      { felder: auswahl, bestaetigung: 'AKTUALISIEREN' },
    );
    if (ergebnis) {
      setLaufId(ergebnis.laufId);
      setBestaetigung('');
    }
  }, [rufen, entwurfId, auswahl]);

  const zuruecknehmen = useCallback(async () => {
    if (!laufId) return;
    const ergebnis = await rufen<FeldRollbackBericht>(
      'Zurücksetzen', `/programmes/artikelimport/laeufe/${laufId}/felder-rollback`,
      { bestaetigung: 'ZURUECKSETZEN' },
    );
    if (ergebnis) setRollback(ergebnis);
  }, [rufen, laufId]);

  const bilderNachziehen = useCallback(async () => {
    const ergebnis = await rufen<NachlieferBericht>(
      'Bilder nachziehen', `/programmes/artikelimport/entwuerfe/${entwurfId}/bilder-nachziehen`,
      { ordner: nachzugOrdner, bestaetigung: 'BILDER' },
    );
    if (ergebnis) setNachzug(ergebnis);
  }, [rufen, entwurfId, nachzugOrdner]);

  const katalogFelder = useMemo(
    () => (katalog?.felder ?? []).filter(f => f.gruppe === 'katalog'),
    [katalog],
  );
  const einstellungsFelder = useMemo(
    () => (katalog?.felder ?? []).filter(f => f.gruppe === 'einstellung'),
    [katalog],
  );

  return (
    <div className="space-y-3">
      {fehler && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900">
          {fehler}
        </div>
      )}

      {/* ── Schritt: Felder ────────────────────────────────────────────────── */}
      <Schritt
        nummer={schrittAb}
        titel="Welche Felder sollen gefüllt werden?"
        zustand={auswahl.length === 0
          ? 'Noch kein Feld gewählt.'
          : `${zahl(auswahl.length)} Felder gewählt${auswahl.some(a => a.ueberschreiben) ? ' — davon mindestens eines mit Überschreiben' : ', nur Lücken füllen'}.`}
        erledigt={auswahl.length > 0}
      >
      <div className="space-y-3">
        <p className="text-sm text-[#1A1A1A]/70">
          Zugeordnet wird über die EAN, ersatzweise über die Herstellernummer — nur wenn
          die eindeutig ist. Preise bleiben außen vor, dafür gibt es plenty-preisupdate.
        </p>
        <p className="text-sm text-[#1A1A1A]/70">{katalog?.hinweis}</p>

        <FeldGruppe
          titel="Aus der Herstellerdatei"
          felder={katalogFelder}
          gewaehlt={gewaehlt}
          umschalten={umschalten}
          ueberschreibenUmschalten={ueberschreibenUmschalten}
        />
        <FeldGruppe
          titel="Aus dem Hersteller-Profil"
          felder={einstellungsFelder}
          gewaehlt={gewaehlt}
          umschalten={umschalten}
          ueberschreibenUmschalten={ueberschreibenUmschalten}
        />

        {katalog && (
          <p className="text-xs text-[#1A1A1A]/60">
            Nicht anfassbar: {katalog.gesperrt.join(', ')} (redaktionelle SEO-Arbeit),
            Preise (plenty-preisupdate), Ursprungsland (zollrechtlich, eigener Vorgang)
            und die externalId (Anker für Bild- und Herkunfts-Zuordnung).
          </p>
        )}
      </div>
      </Schritt>

      {/* ── Schritt: Vorschau ──────────────────────────────────────────────── */}
      <Schritt
        nummer={schrittAb + 1}
        titel="Vorschau — was würde sich ändern?"
        zustand={vorschau
          ? `${zahl(vorschau.betroffen)} Varianten betroffen · ${zahl(vorschau.luecken)} Lücken gefüllt · ${zahl(vorschau.ueberschreibungen)} überschrieben`
          : 'Liest den Ist-Zustand live aus Plenty. Schreibt nichts.'}
        erledigt={vorschau != null}
        gesperrt={auswahl.length === 0}
        gesperrtGrund="Erst Felder wählen."
      >
      <div className="flex flex-wrap items-center gap-3">
        <LaufButton
          variant="outline"
          laeuft={laeuft === 'Vorschau'}
          laufText="Ist-Zustand wird gelesen"
          dauerHinweis="100 Varianten je Leseabfrage gegen Plenty"
          icon={<Eye className="mr-2 h-4 w-4" />}
          onClick={() => void vorschauHolen()}
          disabled={auswahl.length === 0 || laeuft != null}
        >
          Vorschau — was würde sich ändern?
        </LaufButton>
        <span className="text-xs text-[#1A1A1A]/60">
          Liest den Ist-Zustand live aus Plenty. Schreibt nichts.
        </span>
      </div>

      {vorschau && (
        <div className="space-y-3 rounded border border-[#DAD9D9] bg-white p-3">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Kachel label="Varianten betroffen" wert={zahl(vorschau.betroffen)} betont />
            <Kachel label="Lücken gefüllt" wert={zahl(vorschau.luecken)} />
            <Kachel label="überschrieben" wert={zahl(vorschau.ueberschreibungen)} warnen />
            <Kachel label="Schreibvorgänge" wert={zahl(vorschau.calls)} />
          </div>

          <p className="text-sm text-[#1A1A1A]/70">
            Zuordnung: {zahl(vorschau.zuordnung.ueberEan + vorschau.zuordnung.ueberEanLive)} über
            EAN, {zahl(vorschau.zuordnung.ueberNummer)} über die Herstellernummer,{' '}
            {zahl(vorschau.zuordnung.ohneTreffer)} nicht im Bestand,{' '}
            {zahl(vorschau.zuordnung.mehrdeutig)} mehrdeutig.
          </p>

          {[...vorschau.meldungen, ...vorschau.zuordnungsMeldungen].map((m, i) => (
            <p key={i} className="text-sm text-amber-800">{m}</p>
          ))}

          {vorschau.zeilen.length > 0 && (
            <details className="rounded border border-[#DAD9D9] p-2">
              <summary className="cursor-pointer text-sm font-medium text-[#1A1A1A]">
                Vorher / Nachher ({zahl(vorschau.zeilen.length)} Varianten)
              </summary>
              {vorschau.zeilen.length > ZEILEN_DECKEL && (
                <p className="mt-2 text-xs text-amber-800">
                  Angezeigt werden die ersten {zahl(ZEILEN_DECKEL)} von{' '}
                  {zahl(vorschau.zeilen.length)} Varianten. Geschrieben werden ALLE —
                  die Zahlen oben zählen vollständig.
                </p>
              )}
              <div className="mt-2 max-h-80 overflow-y-auto">
                <table className="w-full text-xs">
                  <tbody>
                    {vorschau.zeilen.slice(0, ZEILEN_DECKEL).map(z => (
                      <tr key={z.variationId} className="border-b border-[#DAD9D9]/50 align-top">
                        <td className="py-1 pr-3">{z.bezeichnung}</td>
                        <td className="py-1 pr-3 tabular-nums text-[#1A1A1A]/50">{z.variationId}</td>
                        <td className="py-1">
                          {z.aenderungen.map(a => (
                            <div key={a.feld}>
                              <span className="text-[#1A1A1A]/70">{a.name}:</span>{' '}
                              <span className="text-[#1A1A1A]/50">{a.alt ?? '–'}</span>
                              {' → '}
                              <span className="font-medium">{a.neu}</span>
                              {a.grund === 'ueberschreiben' && (
                                <span className="ml-2 rounded bg-amber-100 px-1 text-amber-900">
                                  überschreibt
                                </span>
                              )}
                            </div>
                          ))}
                          {/*
                            Auch neben Aenderungen anzeigen: eine Variante, bei der ein
                            Feld geschrieben und drei uebersprungen werden, sah vorher
                            aus, als ginge alles durch.
                          */}
                          {z.uebersprungen.map((u, j) => (
                            <div key={j} className="text-[#1A1A1A]/50">{u.grund}</div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}

      </Schritt>

      {/* ── Schritt: Schreiben ─────────────────────────────────────────────── */}
      <Schritt
        nummer={schrittAb + 2}
        titel="In PlentyMarkets schreiben"
        ton="gefahr"
        zustand={laufId
          ? `Update-Lauf ${laufId} gestartet — die alten Werte sind gesichert.`
          : vorschau && vorschau.betroffen > 0
            ? `${zahl(vorschau.betroffen)} Varianten würden geschrieben. Tippbestätigung nötig.`
            : 'Erst die Vorschau ansehen.'}
        erledigt={laufId != null}
        gesperrt={!vorschau || vorschau.betroffen === 0}
        gesperrtGrund={vorschau && vorschau.betroffen === 0 ? 'Die Vorschau hat nichts zu ändern gefunden.' : 'Erst die Vorschau ansehen.'}
      >
      {vorschau && vorschau.betroffen > 0 && !laufId && (
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="mb-1 block text-[#1A1A1A]/70">
              Zum Bestätigen <strong>AKTUALISIEREN</strong> tippen
            </span>
            <input
              type="text"
              value={bestaetigung}
              onChange={e => setBestaetigung(e.target.value)}
              placeholder="AKTUALISIEREN"
              className="rounded border border-[#DAD9D9] px-3 py-2 font-mono"
            />
          </label>
          <LaufButton
            laeuft={laeuft === 'Aktualisieren'}
            laufText="Varianten werden aktualisiert"
            dauerHinweis="erst sichern, dann schreiben — 50 Varianten je Paket"
            icon={<PencilLine className="mr-2 h-4 w-4" />}
            onClick={() => void aktualisieren()}
            disabled={bestaetigung !== 'AKTUALISIEREN' || laeuft != null}
            className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
          >
            {`${zahl(vorschau.betroffen)} Varianten aktualisieren`}
          </LaufButton>
        </div>
      )}

      {laufId && (
        <div className="space-y-2 rounded border border-[#DAD9D9] bg-white p-3 text-sm">
          <p className="text-[#1A1A1A]/80">
            Update-Lauf {laufId} gestartet. Er arbeitet im Hintergrund weiter; die alten
            Werte sind gesichert.
          </p>
          <LaufButton
            variant="outline"
            size="sm"
            laeuft={laeuft === 'Zurücksetzen'}
            laufText="Felder werden zurückgesetzt"
            icon={<Undo2 className="mr-2 h-3.5 w-3.5" />}
            onClick={() => void zuruecknehmen()}
            disabled={laeuft != null}
          >
            Felder zurücksetzen
          </LaufButton>
          <p className="text-xs text-[#1A1A1A]/60">
            Setzt nur die Felder zurück, die dieser Lauf angefasst hat — auf den Wert,
            der davor dastand. Wurde eines davon seitdem von Hand geändert, überschreibt
            das die Änderung.
          </p>
          {rollback && (
            <div className="text-[#1A1A1A]/80">
              <p>
                {zahl(rollback.zurueckgesetzt)} Felder an {zahl(rollback.varianten)} Varianten
                zurückgesetzt{rollback.fehler > 0 && ` · ${zahl(rollback.fehler)} Fehler`}.
              </p>
              {rollback.meldungen.map((m, i) => (
                <p key={i} className="mt-1 text-xs text-[#1A1A1A]/60">{m}</p>
              ))}
            </div>
          )}
        </div>
      )}

      </Schritt>

      {/* ── Schritt: Bilder (optional) ─────────────────────────────────────── */}
      <Schritt
        nummer={schrittAb + 3}
        titel="Bilder an bestehende Artikel hängen (optional)"
        ton="warnung"
        zustand={nachzug
          ? `${zahl(nachzug.hochgeladen)} Bilder hochgeladen, ${zahl(nachzug.verknuepft)}× verknüpft.`
          : 'Rein zusätzlich — es verschwindet kein Feld und kein bestehendes Bild.'}
        erledigt={nachzug != null}
        offen={false}
      >
      <div className="space-y-2">
        <h3 className="flex items-center gap-2 text-sm font-medium text-[#1A1A1A]">
          <Images className="h-4 w-4" /> Bilder an bestehende Artikel hängen
        </h3>
        <p className="text-sm text-[#1A1A1A]/70">
          Rein zusätzlich — es verschwindet kein Feld und kein bestehendes Bild. Für den
          Fall, dass die Artikel längst in Plenty stehen und nur die Fotos fehlen.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={nachzugOrdner}
            onChange={e => setNachzugOrdner(e.target.value)}
            className="rounded border border-[#DAD9D9] px-3 py-2 text-sm"
          >
            <option value="">Bildordner wählen …</option>
            {bildOrdner.filter(o => o.bildAnzahl > 0).map(o => (
              <option key={o.pfad} value={o.pfad}>
                {o.name} ({zahl(o.bildAnzahl)} Bilder)
              </option>
            ))}
          </select>
          <LaufButton
            variant="outline"
            size="sm"
            laeuft={laeuft === 'Bilder nachziehen'}
            laufText="Bilder werden nachgezogen"
            dauerHinweis="ein Upload je Farbe, eine Verknüpfung je Variante"
            onClick={() => void bilderNachziehen()}
            disabled={!nachzugOrdner || laeuft != null}
          >
            Bilder nachziehen
          </LaufButton>
        </div>
        {nachzug && (
          <div className="text-sm text-[#1A1A1A]/80">
            <p>
              {zahl(nachzug.hochgeladen)} Bilder hochgeladen, {zahl(nachzug.verknuepft)}×
              verknüpft{nachzug.schonVerknuepft > 0 && ` · ${zahl(nachzug.schonVerknuepft)} hingen schon`}
              {nachzug.fehler > 0 && ` · ${zahl(nachzug.fehler)} Fehler`} — bei{' '}
              {zahl(nachzug.artikelMitNachschub)} von {zahl(nachzug.artikelBetrachtet)} Artikeln.
            </p>
            {nachzug.meldungen.map((m, i) => (
              <p key={i} className="mt-1 text-xs text-amber-800">{m}</p>
            ))}
          </div>
        )}
      </div>
      </Schritt>
    </div>
  );
}

/**
 * Eine Feldgruppe mit zwei Haken je Zeile.
 *
 * Der zweite Haken erscheint erst, wenn der erste gesetzt ist — „überschreiben" ohne
 * „anfassen" ergibt keinen Sinn und würde nur verwirren.
 */
function FeldGruppe({
  titel, felder, gewaehlt, umschalten, ueberschreibenUmschalten,
}: {
  titel: string;
  felder: Array<{ schluessel: string; name: string; herkunft: string }>;
  gewaehlt: (feld: string) => FeldAuswahl | undefined;
  umschalten: (feld: string) => void;
  ueberschreibenUmschalten: (feld: string) => void;
}) {
  if (felder.length === 0) return null;

  return (
    <div className="space-y-1">
      <p className="text-xs uppercase tracking-wide text-[#1A1A1A]/50">{titel}</p>
      {felder.map(feld => {
        const wahl = gewaehlt(feld.schluessel);
        return (
          <div
            key={feld.schluessel}
            className="flex flex-wrap items-center gap-3 rounded border border-[#DAD9D9] bg-white px-3 py-2 text-sm"
          >
            <label className="flex flex-1 cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={wahl != null}
                onChange={() => umschalten(feld.schluessel)}
              />
              <span>
                <span className="font-medium text-[#1A1A1A]">{feld.name}</span>
                <span className="ml-2 text-xs text-[#1A1A1A]/50">{feld.herkunft}</span>
              </span>
            </label>

            {wahl && (
              <label
                className="flex cursor-pointer items-center gap-2 text-xs"
                title="Ohne diesen Haken wird nur gefüllt, wo Plenty leer oder 0 ist."
              >
                <input
                  type="checkbox"
                  checked={wahl.ueberschreiben === true}
                  onChange={() => ueberschreibenUmschalten(feld.schluessel)}
                />
                <span className={wahl.ueberschreiben ? 'text-amber-900' : 'text-[#1A1A1A]/60'}>
                  belegte Werte überschreiben
                </span>
              </label>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Kachel({
  label, wert, betont, warnen,
}: {
  label: string; wert: string; betont?: boolean; warnen?: boolean;
}) {
  const rahmen = warnen && wert !== '0' ? 'border-amber-400 bg-amber-50'
    : betont ? 'border-[#1A1A1A] bg-[#E9FF57]'
    : 'border-[#DAD9D9] bg-white';
  return (
    <div className={`rounded border p-3 ${rahmen}`}>
      <div className="text-2xl font-bold tabular-nums text-[#1A1A1A]">{wert}</div>
      <div className="text-xs text-[#1A1A1A]/70">{label}</div>
    </div>
  );
}
